import React, { PureComponent } from 'react';
import { I } from '../../../../common/v5/config';
import { UnnestedAreaSingleSelect } from '../../../../reactcomponents/Dropdown';
import HelpTooltip from './HelpTooltip';
import './AgentForm.css';

class AgentForm extends PureComponent {
  constructor(props) {
    super(props);

    // Initialize state with agent data if editing
    const { agent } = props;
    
    this.state = {
      formData: {
        name: agent?.name || agent?.title || '',
        description: agent?.description || '',
        agent_type: this.mapAgentTypeFromAPI(agent?.agent_type || agent?.type) || 'AUTO_ANSWER',
        area: agent?.area_id || agent?.area || '',
        parentAgentId: agent?.parent_agent_id || agent?.parentAgentId || ''
      }
    };

    // Bind methods
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleAreaSelect = this.handleAreaSelect.bind(this);
    this.handleTypeChange = this.handleTypeChange.bind(this);
  }

  // Map API agent types to UI format
  mapAgentTypeFromAPI(apiType) {
    switch (apiType) {
      case 'router':
        return 'ROUTER';
      case 'auto-answer':
        return 'AUTO_ANSWER';
      case 'chat':
        return 'CHAT';
      case 'team-member':
        return 'TEAM_MEMBER';
      case 'supervisor':
        return 'SUPERVISOR';
      case 'agent-assist':
        return 'AGENT_ASSIST';
      default:
        return 'AUTO_ANSWER';
    }
  }

  componentDidUpdate(prevProps) {
    // Update form data when agent prop changes 
    if (prevProps.agent !== this.props.agent) {
      const { agent } = this.props;
      const formData = {
        name: agent?.name || agent?.agent_name || agent?.title || '',
        description: agent?.description || '',
        agent_type: this.mapAgentTypeFromAPI(agent?.agent_type || agent?.type) || '',
        area: agent?.area_id || agent?.area || agent?.AreaID || '',
        parentAgentId: agent?.parent_agent_id || agent?.parentAgentId || ''
      };
      this.setState({ formData });
    }
  }

  // Notify parent of changes
  notifyChange() {
    const { onChange } = this.props;
    if (onChange) {
      const { formData } = this.state;
      onChange(formData);
    }
  }

  handleInputChange(field, value) {
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [field]: value
      }
    }), () => {
      this.notifyChange();
    });
  }

  handleAreaSelect(areaId) {
    this.handleInputChange('area', areaId);
  }

  handleTypeChange(e) {
    const newType = e.target.value;
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        agent_type: newType,
        // Clear parent agent if router is selected (router agents cannot have parents)
        parentAgentId: newType === 'ROUTER' ? '' : prevState.formData.parentAgentId
      }
    }), () => {
      this.notifyChange();
    });
  }

  render() {
    const { areaList = [], agent } = this.props;
    const { formData } = this.state;
    // Only disable name when editing an existing agent (non-zero id). Create New Agent has id: 0.
    const isEditing = !!(
      agent &&
      ((agent.id != null && agent.id !== 0 && agent.id !== '0') ||
        (agent.agent_id != null && agent.agent_id !== 0 && agent.agent_id !== '0'))
    );

    return (
      <div className="admin-one-form custom">
        <div className="agent-manager">
          {/* Agent Name */}
          <div className="form-input-with-label-row">
            <div className="form-label mandatory">
              {I("Agent Name")}
            </div>
            <div className="form-input-container">
              <input
                type="text"
                id="agentName"
                name="agentName"
                value={formData.name}
                onChange={(e) => this.handleInputChange('name', e.target.value)}
                className="admin-text-input"
                placeholder={I("Enter agent name")}
                disabled={isEditing}
                style={isEditing ? { backgroundColor: '#f5f5f5', color: '#999', cursor: 'not-allowed' } : {}}
              />
            </div>
          </div>

          {/* Agent Description */}
          <div className="form-input-with-label-row">
            <div className="form-label mandatory">
              {I("Agent Description")}
            </div>
            <div className="form-input-container">
              <textarea
                id="agentDescription"
                name="agentDescription"
                value={formData.description}
                onChange={(e) => this.handleInputChange('description', e.target.value)}
                className="admin-text-input"
                rows={4}
                placeholder={I("Enter agent description")}
              />
            </div>
          </div>

          {/* Agent Type */}
          <div className="form-input-with-label-row">
            <div className="form-label mandatory">
              {I("Agent Type")}
          <HelpTooltip 
            content={`<strong>${I("Default:")}</strong> ${I("Standard agent type")}<br/><strong>${I("Router:")}</strong> ${I("Routes conversations to appropriate agents")}`}
            position="top"
          />
            </div>
            <div className="form-input-container">
              <select
                id="agentType"
                value={formData.agent_type}
                onChange={this.handleTypeChange}
                className="form-control styled-select"
              >
                <option value="">{I('Default')}</option>
                <option value="ROUTER">{I('Router')}</option>
              </select>
            </div>
          </div>

          {/* Area Selection */}
          <div className="form-input-with-label-row">
            <div className="form-label mandatory">
              {I("Area")}
          <HelpTooltip 
            content={I("Select the area this agent belongs to. This determines access permissions and routing rules.")}
            position="top"
          />
            </div>
            <div className="form-input-container">
              <UnnestedAreaSingleSelect
                data={areaList}
                invalidSelectedText={I('Select area...')}
                onSelect={this.handleAreaSelect}
                selected={formData.area}
                nested={{key: 'Areas'}}
                idFields={{id: "Id", name: "Name"}}
              />
            </div>
          </div>

          {/* Parent Agent Selection */}
          <div className="form-input-with-label-row">
            <div className="form-label">
              {I("Parent Agent")} <span className="optional-text">({I("Optional")})</span>
              <HelpTooltip 
                content={formData.agent_type === 'ROUTER' 
                  ? I("Router agents cannot have a parent. They must be at the top level of the hierarchy.")
                  : I("Select a parent agent to create a hierarchical structure. Leave empty for root agents.")
                }
                position="top"
              />
            </div>
            <div className="form-input-container">
              <select
                id="parentAgent"
                value={formData.parentAgentId || ''}
                onChange={(e) => this.handleInputChange('parentAgentId', e.target.value)}
                className="form-control styled-select"
                disabled={formData.agent_type === 'ROUTER'}
              >
                <option value="">{I('No parent (root agent)')}</option>
                {this.props.agents && this.props.agents.filter(agent => 
                  // Don't allow self-reference when editing
                  !this.props.agent || (agent.id || agent.agent_id) !== (this.props.agent.id || this.props.agent.agent_id)
                ).map((agent) => (
                  <option key={agent.id || agent.agent_id} value={agent.agent_id || agent.id}>
                    {agent.name || agent.title || agent.agent_name || agent.agent_id} - {agent.description}
                  </option>
                ))}
              </select>
              {formData.agent_type === 'ROUTER' && (
                <div className="form-help-text" style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                  {I('Router agents are automatically set as root agents (no parent).')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default AgentForm; 